import type { Report } from '@k-ucap/shared';

export class ReportExportService {
  static toCsv(report: Report): string {
    const lines: string[] = [
      'K-UCAP Simulation Report',
      `Title,${report.title}`,
      `Simulation Run ID,${report.simulationRunId}`,
      `Generated At,${report.createdAt}`,
      '',
      'Summary',
      'Total Messages,Sent,Dropped,PASS,WARN,FAIL,Duration (ms)',
      `${report.summary.totalMessages},${report.summary.sentCount},${report.summary.droppedCount},${report.summary.passCount},${report.summary.warnCount},${report.summary.failCount},${report.summary.durationMs}`,
      '',
      'Message Type Statistics',
      'Message Type,Count,PASS,WARN,FAIL',
      ...Object.entries(report.messageTypeStats).map(([type, s]) => `${type},${s.count},${s.pass},${s.warn},${s.fail}`),
      '',
      'Alerts',
      'Severity,Message,Vehicle ID,Timestamp',
      ...report.alerts.map((a) => `${a.severity},${a.message},${a.vehicleId ?? ''},${a.timestamp}`),
    ];
    return lines.join('\n');
  }

  static toJson(report: Report): string {
    return JSON.stringify(report, null, 2);
  }

  static toHtml(report: Report): string {
    const rows = Object.entries(report.messageTypeStats)
      .map(
        ([type, s]) =>
          `<tr><td>#${type}</td><td>${s.count}</td><td>${s.pass}</td><td>${s.warn}</td><td>${s.fail}</td></tr>`
      )
      .join('');

    const total = report.summary.totalMessages || 1;
    const passPct = Math.round((report.summary.passCount / total) * 100);
    const warnPct = Math.round((report.summary.warnCount / total) * 100);
    const failPct = Math.round((report.summary.failCount / total) * 100);

    const failureEvidence =
      report.fieldErrors
        .map((e) => `<tr><td>#${e.messageType}</td><td>${e.field}</td><td>${e.count}</td></tr>`)
        .join('') || '<tr><td colspan="3">없음</td></tr>';

    const roleErrors =
      report.roleErrors
        .map((e) => `<tr><td>#${e.messageType}</td><td>${e.expected}</td><td>${e.actual}</td><td>${e.count}</td></tr>`)
        .join('') || '<tr><td colspan="4">없음</td></tr>';

    return `
      <html>
        <head>
          <title>${report.title}</title>
          <style>
            @media print {
              body { -webkit-print-color-adjust: exact; }
              .page-break { page-break-before: always; }
              .no-break { page-break-inside: avoid; }
            }
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; margin: 40px; color: #1a1a1a; }
            h1 { font-size: 28px; margin-bottom: 8px; }
            h2 { font-size: 20px; margin-top: 32px; margin-bottom: 12px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
            .cover { text-align: center; padding: 80px 0; }
            .cover h1 { font-size: 36px; }
            .cover .meta { color: #666; margin-top: 24px; }
            .cover .result { font-size: 48px; font-weight: bold; margin-top: 40px; }
            .cover .result-pass { color: #2e7d32; }
            .cover .result-warn { color: #ed6c02; }
            .cover .result-fail { color: #d32f2f; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background: #f5f5f5; }
            .chart { display: flex; height: 32px; border-radius: 4px; overflow: hidden; margin: 16px 0; }
            .chart-pass { background: #4caf50; }
            .chart-warn { background: #ff9800; }
            .chart-fail { background: #f44336; }
            .legend { display: flex; gap: 16px; margin-top: 8px; font-size: 14px; }
            .legend span { display: flex; align-items: center; gap: 4px; }
            .dot { width: 12px; height: 12px; border-radius: 50%; display: inline-block; }
          </style>
        </head>
        <body>
          <div class="cover">
            <h1>${report.title}</h1>
            <div class="meta">
              <p>Project ID: ${report.projectId}</p>
              <p>Simulation Run ID: ${report.simulationRunId}</p>
              <p>Generated: ${new Date(report.createdAt).toLocaleString()}</p>
            </div>
            <div class="result ${report.summary.failCount > 0 ? 'result-fail' : report.summary.warnCount > 0 ? 'result-warn' : 'result-pass'}">
              ${report.summary.failCount > 0 ? 'FAIL' : report.summary.warnCount > 0 ? 'WARN' : 'PASS'}
            </div>
          </div>

          <div class="page-break"></div>

          <h2>Summary</h2>
          <table>
            <tr><th>Total</th><th>Sent</th><th>Dropped</th><th>PASS</th><th>WARN</th><th>FAIL</th><th>Duration (ms)</th></tr>
            <tr>
              <td>${report.summary.totalMessages}</td>
              <td>${report.summary.sentCount}</td>
              <td>${report.summary.droppedCount}</td>
              <td>${report.summary.passCount}</td>
              <td>${report.summary.warnCount}</td>
              <td>${report.summary.failCount}</td>
              <td>${report.summary.durationMs}</td>
            </tr>
          </table>

          <h2>Result Distribution</h2>
          <div class="chart">
            <div class="chart-pass" style="width: ${passPct}%"></div>
            <div class="chart-warn" style="width: ${warnPct}%"></div>
            <div class="chart-fail" style="width: ${failPct}%"></div>
          </div>
          <div class="legend">
            <span><span class="dot" style="background:#4caf50"></span> PASS ${report.summary.passCount} (${passPct}%)</span>
            <span><span class="dot" style="background:#ff9800"></span> WARN ${report.summary.warnCount} (${warnPct}%)</span>
            <span><span class="dot" style="background:#f44336"></span> FAIL ${report.summary.failCount} (${failPct}%)</span>
          </div>

          <h2>Message Type Statistics</h2>
          <table>
            <tr><th>Type</th><th>Count</th><th>PASS</th><th>WARN</th><th>FAIL</th></tr>
            ${rows}
          </table>

          <h2>Failure Evidence</h2>
          <h3>Field Errors</h3>
          <table>
            <tr><th>Message Type</th><th>Field</th><th>Count</th></tr>
            ${failureEvidence}
          </table>

          <h3>Role Errors</h3>
          <table>
            <tr><th>Message Type</th><th>Expected</th><th>Actual</th><th>Count</th></tr>
            ${roleErrors}
          </table>

          <h2>Alerts</h2>
          <table>
            <tr><th>Severity</th><th>Message</th><th>Vehicle ID</th><th>Timestamp</th></tr>
            ${report.alerts.map((a) => `<tr><td>${a.severity}</td><td>${a.message}</td><td>${a.vehicleId ?? ''}</td><td>${a.timestamp}</td></tr>`).join('') || '<tr><td colspan="4">없음</td></tr>'}
          </table>
        </body>
      </html>
    `;
  }
}

export default ReportExportService;
